import { DriveFileMove } from '@mui/icons-material';
import { Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useState } from "react";
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { ConfirmationButton } from '../../components/ConfirmationButton';
import { normaliseFieldrenders } from "../../components/CreateButtonProps";
import EasyForm from "../../components/Forms/EasyForm";
import EasyFormColumn from "../../components/Forms/EasyFormColumn";
import { FormType } from "../../components/Forms/EasyFormMany";
import { AutocompleteFieldRender } from "../../components/Forms/Renders/AutocompleteFieldRender";
import { TextFieldRender } from '../../components/Forms/Renders/TextFieldRender';
import Loading from '../../components/loading';
import { PaginationQueryPackage } from "../../models/API/QueryParams/PaginationQueryPackage";
import HostingsiteBase, { HostingsiteResponse } from "../../models/Hostingsite";
import MoveSubnetRequest from '../../models/MoveSubnetRequest';
import { Subnet } from '../../models/Subnet';
import { useGetSubnetsByHostingsiteIdQuery, useMoveSubnetToExistingHSMutation, useMoveSubnetToNewHSMutation } from '../OrganisationApi/redux/subnetApiSlice';
import { useGetHostingsitesByOrganisationIdQuery } from './hostingsitesApiSlice';

interface moveSubnetProps {
    hostingSite: HostingsiteResponse
}

export const MoveSubnet = (props: moveSubnetProps) => {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [newHS, setNewHS] = useState(true)

    const { data: subnets } = useGetSubnetsByHostingsiteIdQuery({
        pagination: new PaginationQueryPackage(),
        uuid: props.hostingSite.uuid
    });

    const { data: hostingSites } = useGetHostingsitesByOrganisationIdQuery({
        pagination: new PaginationQueryPackage(),
        uuid: props.hostingSite.owner_organisation?.uuid
    });

    if (subnets == undefined || hostingSites == undefined) {
        return <Loading />
    }

    const hsOptions = hostingSites
        .filter(hs => hs.owner_organisation?.uuid == props.hostingSite.owner_organisation?.uuid && hs.uuid != props.hostingSite.uuid)

    const subnetRender = new AutocompleteFieldRender({
        displayText: "Subnet",
        fieldName: "",
        getOptionLabelFunc: (option: Subnet) => option.subnet ?? "",
        renderOption: (_props, option) => option.subnet,
        nestedfieldName: "subnet",
        options: subnets ?? [],
        uniqueId: crypto.randomUUID()
    })

    const moveToNewHSRows = () => {
        return [
            subnetRender,
            new TextFieldRender({
                displayText: t("Hosting site name"),
                fieldName: "hosting_site_name",
                type: "text",
                uniqueId: crypto.randomUUID()
            }),
            new TextFieldRender({
                displayText: t("Hosting site address"),
                fieldName: "hosting_site_address",
                type: "text",
                uniqueId: crypto.randomUUID(),
            }),
        ];
    }

    const moveToExistingHSRows = () => {
        return [
            subnetRender,
            new AutocompleteFieldRender({
                displayText: "Hosting site",
                fieldName: "hosting_site",
                getOptionLabelFunc: (option: HostingsiteBase) => option.name ?? "",
                renderOption: (_props, option) => option.name,
                options: hsOptions ?? [],
                uniqueId: crypto.randomUUID()
            }),
        ];
    }

    const moveToNewHSSchema = Yup.object().shape({
        subnet: Yup.object().nullable().required(t("Required")),
        hosting_site_name: Yup.string().required(t("Required")),
        hosting_site_address: Yup.string().required(t("Required")),
    });

    const moveToExistingHSSchema = Yup.object().shape({
        subnet: Yup.object().nullable().required(t("Required")),
        hosting_site: Yup.object().nullable().required(t("Required")),
    });

    return (

        <ConfirmationButton
            childrenOpen={open}
            setChildrenOpen={setOpen}
            asMenuItem
            title={t("Move subnet")}
            buttonText={<><DriveFileMove />{t("Move subnet")}</>}
            key={"moveSubnet"}
            showButtons={false}
            fullWidth
            maxWidth="md"
        >
            <>
                <ToggleButtonGroup
                    color="primary"
                    exclusive
                    value={newHS ? "newHS" : "existingHS"}
                    onChange={(_, value) => setNewHS(value == "newHS")}
                >
                    <ToggleButton value="newHS">{t("New Hosting Site")}</ToggleButton>
                    <ToggleButton value="existingHS">{t("Existing Hosting Site")}</ToggleButton>
                </ToggleButtonGroup>
                <EasyForm
                    isAllowedToEdit={() => true}
                    editModeAlways={true}
                    arrayPath=""
                    ressourceOwnerId={props.hostingSite.owner_organisation?.uuid}
                    SignupSchema={newHS ? moveToNewHSSchema : moveToExistingHSSchema}
                    initialValue={new MoveSubnetRequest()}
                    columns={[
                        new EasyFormColumn("", "",
                            newHS ? normaliseFieldrenders(moveToNewHSRows()) : normaliseFieldrenders(moveToExistingHSRows())),
                    ]}
                    formType={FormType.INFORMATION}
                    getFieldRows={() => []}
                    saveMutation={newHS ? useMoveSubnetToNewHSMutation : useMoveSubnetToExistingHSMutation}
                    createNew={() => { throw new Error("Dont create new here") }}
                    afterSaveAction={() => {
                        setOpen(false)
                    }}
                    extraButtons={<Button onClick={() => setOpen(false)}>{t("Cancel")}</Button>}
                    displayTextWidth='40%'
                />
            </>
        </ConfirmationButton>
    );
}